import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clapperboard, Mic2 } from 'lucide-react';
import { api, type RecordingHistoryItem } from '../../lib/api';
import {
  dayRange,
  HISTORY_PAGE_SIZES,
  pageWindow,
  showingRange,
  walkthroughHeadline,
  walkthroughLength,
  walkthroughVideo,
  walkthroughVideoLabel,
} from './walkthroughHistory';
import styles from './PastWalkthroughs.module.css';
import { keptFor, useVideoRetentionDays } from './videoRetention';
import { formatWhen } from '../../lib/time';


/**
 * The walkthroughs somebody has finished, so they can get back to one (TOADO-439).
 *
 * Nothing listed a walkthrough once it committed. The recorder page shows only what
 * is still in flight, and the only way back to a finished one was the link the
 * extension opened as it finished. Each row opens that walkthrough's own page, which
 * still shows its write-ups, screenshots and tickets once the video has gone - so
 * the list says plainly what Toado keeps, and for how long.
 *
 * Numbered pages of 20, 50 or 100, because somebody can have thousands of these, and
 * a project and a range of days to narrow them to (TOADO-441). Every one of those is
 * asked of the server, so the count, the pages and the filters describe all of them
 * rather than the page in hand. The page being left stays on screen, dimmed, until
 * the next one arrives, rather than the list collapsing to a spinner between pages.
 */
export function PastWalkthroughs({
  companyId,
  projects,
}: {
  companyId: string;
  projects: ReadonlyArray<{ id: string; name: string }>;
}) {
  const [page, setPage] = useState(1);
  const retentionDays = useVideoRetentionDays(companyId);
  const [pageSize, setPageSize] = useState<number>(HISTORY_PAGE_SIZES[0]);
  const [projectId, setProjectId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const section = useRef<HTMLElement>(null);
  const moved = useRef(false);
  const range = dayRange(fromDate, toDate);
  const filtered = Boolean(projectId || fromDate || toDate);
  const projectNames = useMemo(() => new Map(projects.map((project) => [project.id, project.name])), [projects]);

  const history = useQuery({
    queryKey: ['recordings', 'history', companyId, page, pageSize, projectId, range.from, range.to],
    queryFn: ({ signal }) => api.listRecordingHistory({
      companyId,
      page,
      pageSize,
      ...(projectId ? { projectId } : {}),
      ...range,
    }, signal),
    placeholderData: keepPreviousData,
  });
  const data = history.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  // A page emptied by walkthroughs going away since it was counted: go to the last one.
  useEffect(() => {
    if (data && !history.isPlaceholderData && page > totalPages) setPage(totalPages);
  }, [data, history.isPlaceholderData, page, totalPages]);

  // Paging from the controls at the bottom brings the top of the new page into view.
  useEffect(() => {
    if (!moved.current) return;
    section.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [page]);
  const goTo = (next: number) => {
    moved.current = true;
    setPage(next);
  };
  // Any change to what is asked for starts again at the first page of the answer.
  const narrow = (apply: () => void) => {
    apply();
    setPage(1);
  };
  const clearFilters = () => narrow(() => {
    setProjectId('');
    setFromDate('');
    setToDate('');
  });

  const items = data?.items ?? [];
  return (
    <section ref={section} className={styles.section} aria-labelledby="past-walkthroughs-title">
      <div className={styles.heading}>
        <h2 id="past-walkthroughs-title">Past walkthroughs</h2>
        {data && (data.total > 0 || filtered) && (
          <small>
            {data.total.toLocaleString()} {filtered ? 'matching' : data.total === 1 ? 'walkthrough' : 'walkthroughs'}
          </small>
        )}
      </div>

      <div className={styles.keeps}>
        <strong>What Toado keeps</strong>
        <ul>
          <li>Each item&rsquo;s video, {keptFor(retentionDays, 'for as long as your plan keeps walkthrough video')}{retentionDays ? ' after it is recorded' : ''}.</li>
          <li>The full-length recording, until every item&rsquo;s clip has been cut from it.</li>
          <li>The write-ups, screenshots and tickets, for as long as the tickets exist.</li>
        </ul>
        <small>A walkthrough you discard is deleted, and is not listed here.</small>
      </div>

      <div className={styles.filters} role="search" aria-label="Filter past walkthroughs">
        <label className={styles.filter}>
          <span>Project</span>
          <select value={projectId} onChange={(event) => { const next = event.target.value; narrow(() => setProjectId(next)); }}>
            <option value="">All projects</option>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
        </label>
        <label className={styles.filter}>
          <span>From</span>
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(event) => { const next = event.target.value; narrow(() => setFromDate(next)); }}
          />
        </label>
        <label className={styles.filter}>
          <span>To</span>
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(event) => { const next = event.target.value; narrow(() => setToDate(next)); }}
          />
        </label>
        {filtered && (
          <button type="button" className={styles.clear} onClick={clearFilters}>Clear</button>
        )}
      </div>

      {history.isPending ? (
        <p className={styles.status}>Loading your walkthroughs…</p>
      ) : history.isError && !data ? (
        <p className={styles.status} role="alert">
          Your walkthroughs could not be loaded.{' '}
          <button type="button" onClick={() => { void history.refetch(); }}>Try again</button>
        </p>
      ) : items.length === 0 ? (
        filtered ? (
          <p className={styles.status}>
            No walkthroughs match these filters.{' '}
            <button type="button" onClick={clearFilters}>Clear filters</button>
          </p>
        ) : (
          <p className={styles.status}>Walkthroughs you finish will be listed here.</p>
        )
      ) : (
        <ul
          className={styles.list}
          aria-busy={history.isPlaceholderData || undefined}
          data-loading={history.isPlaceholderData ? 'true' : undefined}
        >
          {items.map((item) => (
            <li key={item.id}>
              <PastWalkthroughRow item={item} projectName={projectNames.get(item.projectId)} />
            </li>
          ))}
        </ul>
      )}

      {data && data.total > HISTORY_PAGE_SIZES[0] && (
        <nav className={styles.pages} aria-label="Pages of past walkthroughs">
          <div className={styles.pageInfo}>
            <span className={styles.range}>{showingRange(data.page, data.pageSize, data.total, items.length)}</span>
            <label className={styles.perPage}>
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(event) => { const next = Number(event.target.value); narrow(() => setPageSize(next)); }}
              >
                {HISTORY_PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </label>
          </div>
          {totalPages > 1 && (
            <div className={styles.pageButtons}>
              <button type="button" disabled={page <= 1} onClick={() => goTo(page - 1)}>
                <ChevronLeft size={14} aria-hidden="true" /> Previous
              </button>
              {pageWindow(page, totalPages).map((entry, index) => (entry === 'gap' ? (
                <span key={`gap-${index}`} className={styles.gap} aria-hidden="true">…</span>
              ) : (
                <button
                  key={entry}
                  type="button"
                  className={styles.pageNumber}
                  aria-label={`Page ${entry}`}
                  aria-current={entry === page ? 'page' : undefined}
                  onClick={() => goTo(entry)}
                >
                  {entry}
                </button>
              )))}
              <button type="button" disabled={page >= totalPages} onClick={() => goTo(page + 1)}>
                Next <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          )}
        </nav>
      )}
    </section>
  );
}

function PastWalkthroughRow({ item, projectName }: { item: RecordingHistoryItem; projectName: string | undefined }) {
  // A frame of the recording, its first ticket's picture (16 Oct); the icon when there is none or it will not load.
  const [pictureFailed, setPictureFailed] = useState(false);
  const video = walkthroughVideo(item);
  const length = walkthroughLength(item.startedAt, item.endedAt);
  const facts = [
    formatWhen(item.startedAt),
    projectName,
    length,
    `${item.ticketCount} ${item.ticketCount === 1 ? 'ticket' : 'tickets'}`,
  ].filter((fact): fact is string => Boolean(fact));
  return (
    <Link className={styles.row} to={`/recordings/${encodeURIComponent(item.id)}/review`}>
      <span className={styles.picture} aria-hidden="true">
        {item.pictureUrl && !pictureFailed
          ? <img src={item.pictureUrl} alt="" loading="lazy" onError={() => setPictureFailed(true)} data-testid="past-walkthrough-picture" />
          : item.mediaMode === 'audio' ? <Mic2 size={20} /> : <Clapperboard size={20} />}
      </span>
      <span className={styles.copy}>
        <strong>{walkthroughHeadline(item)}</strong>
        <span>{facts.join(' · ')}</span>
      </span>
      <span className={styles.video} data-video={video.kind}>
        {walkthroughVideoLabel(video, item.mediaMode, (iso) => formatWhen(iso, { dateOnly: true }))}
      </span>
      <ChevronRight size={16} className={styles.chevron} aria-hidden="true" />
    </Link>
  );
}
